import { Injectable, Output, EventEmitter } from "@angular/core";
import { MatDialog } from "@angular/material";
import * as clone from "clone";

import { User, createUser, UserCreateRequest, UserUpdateRequest } from "@impulse/sdp-shared-models";

import { ApiGatewayService } from "../../../common/api-gateway/api-gateway.service";
import { TranslateService } from "../../../common/translate/translate.service";
import { SnackbarService } from "../../../common/snackbar/snackbar.service";
import { AuthenticationService } from "../../../common/authentication/authentication.service";

@Injectable()
export class UserWizardService {
	public readonly PARENT_ROUTE = "users";

	@Output() public submitSuccessEvent: EventEmitter<string> = new EventEmitter(true);

	private _user!: User;
	private _isCreate = false;
	private _showProgressBar = false;

	public roleList: string[] = [];
	public userEdit!: UserCreateRequest;

	constructor(
		public dialog: MatDialog,
		private apiGatewayService: ApiGatewayService,
		private translateService: TranslateService,
		private snackbarService: SnackbarService,
		private authService: AuthenticationService
	) { }

	public get user() {
		return this._user;
	}

	public get isCreate() {
		return this._isCreate;
	}

	public get isPrimary() {
		return this._user.primary;
	}

	public get isCurrentUser() {
		const currentUser = this.authService.getCurrentUser();
		// should be impossible, but if we call this while not logged in then we can't be editing the current user
		if (!currentUser) {
			return false;
		}
		return !this.isCreate && this._user.email === currentUser.email;
	}

	public get showProgressBar() {
		return this._showProgressBar;
	}

	public getValue<T extends keyof User>(key: T, defaultValue: User[T]) {
		if (this._user) {
			const data = this._user;
			return data[key] !== undefined ? data[key] : defaultValue;
		}
		return defaultValue;
	}

	public processUserWizardEdit(userEdit: UserCreateRequest) {
		this.userEdit = userEdit;
	}

	public async processGroupsToUsersSubmit() {
		try {
			this._showProgressBar = true;

			try {
				if (this.isCreate) {
					await this.apiGatewayService.createUser(this.userEdit);
				} else {
					const userUpdateRequest: UserUpdateRequest = {
						firstName: this.userEdit.firstName,
						lastName: this.userEdit.lastName,
						admin: this.userEdit.admin,
						disabled: this.userEdit.disabled,
						hideWizard: this.userEdit.hideWizard
					};
					await this.apiGatewayService.updateUser(userUpdateRequest, this.userEdit.email);
				}
			} catch (e) {
				this.snackbarService.open(this.translateService.translate(e));
			} finally {
				this._showProgressBar = false;
			}

			this.closeDialog();
		} catch (e) {
			this.snackbarService.open(this.translateService.translate(e));
		}
	}

	public newUser() {
		this._user = createUser("", "", "", false, false);
		this._isCreate = true;
	}

	public editUser(user: User) {
		this._user = clone(user);
		this._isCreate = false;
	}

	private closeDialog() {
		this.dialog.closeAll();
	}
}
